import { useEffect, useState, useMemo } from 'react';
import { standardizeItemSearch, getSearchFieldsForContext, SEARCH_CONFIG } from 'app/utils/itemSearch';

import useDebounce from '../../../../../hooks/useDebounce';
import {
  AssetItemSchema,
  AssetKitItemSchema,
  InputMaybe,
  InventoryItemSchema,
  InventoryKitItemSchema,
  ItemFilters,
} from '../../../../../types/schema';
import { suggestionData } from '../../../../components/EntityManager/EntityManagerSearch';
import { useItemsSearchDialogSuggestionQuery } from '../graphql/queries/generated/itemsSearchDialogSuggestion';

function parseSearchInput(search: string) {
  return (search || '').replace(/['"]+/g, '');
}

const useSearchSuggestion = (filters: InputMaybe<ItemFilters>, search: string) => {
  const [searchValue, setSearchValue] = useState<string>('');
  const [searchSuggestions, setSearchSuggestions] = useState<suggestionData[]>();

  useEffect(() => {
    setSearchValue(parseSearchInput(search));
  }, [search]);

  const searchedDebounce = useDebounce(searchValue, SEARCH_CONFIG.DEBOUNCE_DELAY);

  const searchContext = useMemo(() => {
    return filters?.types?.includes('ASSET' as any) ? 'asset' : 'inventory';
  }, [filters?.types]);

  const [{ data }] = useItemsSearchDialogSuggestionQuery({
    variables: {
      filters: {
        search: searchedDebounce,
        ...filters,
      },
      limit: SEARCH_CONFIG.DEFAULT_LIMIT, // Use standardized limit
    },
    pause: searchedDebounce.length < SEARCH_CONFIG.MIN_SEARCH_LENGTH, // Only search when minimum length is met
  });

  useEffect(() => {
    if (data?.items?.edges) {
      const items = data.items.edges.map(edge => edge.node as
        | AssetItemSchema
        | AssetKitItemSchema
        | InventoryItemSchema
        | InventoryKitItemSchema);

      const searchResults = standardizeItemSearch(
        items,
        searchValue,
        getSearchFieldsForContext(searchContext)
      );

      const result = searchResults.map(({ label, id, value }) => ({
        label: label || items.find(item => item.id === id)?.title || '',
        id,
        value: value || items.find(item => item.id === id)?.title || '',
      }));

      setSearchSuggestions(result);
    } else {
      setSearchSuggestions([]);
    }
  }, [data, searchValue, searchContext]); // Removed filters from dependency array

  return {
    searchSuggestions,
    setSearchValue,
  };
};

export default useSearchSuggestion;
